import type {TFunction} from 'i18next';

const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
};

const weekdayOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
};

const fullDateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const startOfDay = (date: Date): number =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/**
 * Formats a date relative to `now`:
 * - today: `Today, 08:05`
 * - yesterday: `Yesterday, 08:05`
 * - 2-6 days ago: weekday name, e.g. `Thursday, 08:05`
 * - a week or more ago: `May 15, 2026 10:56`
 *
 * The weekday name is only shown when it is unambiguous (up to 6 days ago),
 * so a date on the same weekday a week ago falls back to the full date.
 *
 * Each branch is a fully translatable template with `{{time}}`, `{{weekday}}`
 * and `{{date}}` placeholders, so translations (e.g. RTL languages) can
 * reorder the parts as needed.
 */
const toRelativeDateTime = (
    value: Date | number,
    {t, language}: {t: TFunction; language: string | undefined},
    now: Date = new Date(),
): {relativeFormattedDateTime: string; fullFormattedDateTime: string} => {
    const date = value instanceof Date ? value : new Date(value);
    const dayDiff = Math.round(
        (startOfDay(now) - startOfDay(date)) / MS_PER_DAY,
    );
    const timeFormat = new Intl.DateTimeFormat(language, timeOptions);
    const weekdayFormat = new Intl.DateTimeFormat(language, weekdayOptions);
    const fullDateFormat = new Intl.DateTimeFormat(language, fullDateOptions);
    const time = timeFormat.format(date);

    const fullFormattedDateTime: string = t(
        'media.date-time.full',
        '{{date}} {{time}}',
        {
            date: fullDateFormat.format(date),
            time,
        },
    );

    if (dayDiff === 0) {
        return {
            relativeFormattedDateTime: t(
                'media.date-time.today',
                'Today, {{time}}',
                {time},
            ),
            fullFormattedDateTime,
        };
    }

    if (dayDiff === 1) {
        return {
            relativeFormattedDateTime: t(
                'media.date-time.yesterday',
                'Yesterday, {{time}}',
                {time},
            ),
            fullFormattedDateTime,
        };
    }

    if (dayDiff >= 2 && dayDiff <= 6) {
        return {
            relativeFormattedDateTime: t(
                'media.date-time.weekday',
                '{{weekday}}, {{time}}',
                {
                    weekday: weekdayFormat.format(date),
                    time,
                },
            ),
            fullFormattedDateTime,
        };
    }

    return {
        relativeFormattedDateTime: fullFormattedDateTime,
        fullFormattedDateTime,
    };
};

export {toRelativeDateTime};
